const ORDINANCE_ORDER = {
  baptism: 1,
  confirmation: 2,
  initiatory: 3,
  endowment: 4,
  sealingToParents: 5,
  sealingToSpouse: 6,
}

/**
 * Determine if the given reservation or row is considered in a waiting state.
 * @param {object} familyGroupReservation - The reservation or row from a family group.
 * @returns {boolean} - is the reservation in a waiting state.
 */
export function isFamilyGroupReservationWaiting(familyGroupReservation) {
  return Object.values(familyGroupReservation.displayedOrdinances || familyGroupReservation.ordinances || {}).every(
    (ordinance) => familyGroupReservation.cpUserId !== ordinance.owner && !ordinance.canReserve
  )
}

/**
 * Returns the ordinances to display for a family group row.
 * @param {object} reservation - The reservation row object.
 * @param {string} groupId - The current group id
 * @returns {object} - the ordinances to display
 */
export const getGroupDisplayedOrdinances = (reservation, groupId = '') => {
  let ordinancesToDisplay = {}
  if (reservation?.type !== 'sealingToSpouse' && reservation.ordinances) {
    Object.entries(reservation.displayedOrdinances || reservation.ordinances).forEach(([key, ord]) => {
      if (ord.groupId === groupId) {
        ordinancesToDisplay[key] = ord
      }
    })
  } else {
    ordinancesToDisplay = (reservation.displayedOrdinances || reservation.ordinances) ?? {}
  }
  return ordinancesToDisplay
}
/**
 * Returns the ordinances to display for a shared to group reservation row.
 * @param {object} reservation - The reservation row object.
 * @returns {object} - The ordinances to display on the shared to group tab on the reservations page
 */
export const getNotSharedReservationOrdinances = (reservation) => {
  let ordinancesToDisplay = {}
  if (reservation?.type !== 'sealingToSpouse' && reservation.ordinances) {
    Object.entries(reservation.displayedOrdinances || reservation.ordinances).forEach(([key, ord]) => {
      if (!ord.groupId || ord.alternateReservationDetails?.ownerId === reservation.cpUserId) {
        ordinancesToDisplay[key] = ord
      }
    })
  } else {
    ordinancesToDisplay = (reservation.displayedOrdinances || reservation.ordinances) ?? {}
  }
  return ordinancesToDisplay
}
/**
 * Returns the ordinances to display for a shared to group reservation row.
 * @param {object} reservation - The reservation row object.
 * @returns {object} - The ordinances to display on the shared to group tab on the reservations page
 */
export const getSharedReservationOrdinances = (reservation) => {
  let ordinancesToDisplay = {}
  if (reservation?.type !== 'sealingToSpouse' && reservation.ordinances) {
    Object.entries(reservation.displayedOrdinances || reservation.ordinances).forEach(([key, ord]) => {
      if (ord.groupId) {
        ordinancesToDisplay[key] = ord
      }
    })
  } else {
    ordinancesToDisplay = (reservation.displayedOrdinances || reservation.ordinances) ?? {}
  }
  return ordinancesToDisplay
}

/**
 * Returns the reservations that are reservable in the formate that is being expected in the reserve call in ordinances reserve package.
 * @param {Array} reservations - An Array of munged reservations.
 * @param {object} selectedOrdinances - An object of ordinance types with a value of T/F.
 * @returns {object} - The ordinances reserve expected object to calculate the payload.
 */
export function getSelectedOrdinances(reservations, selectedOrdinances) {
  return reservations.map((res) => {
    const newOrdinancesObj = {}
    Object.keys(selectedOrdinances).forEach((selectedOrdinance) => {
      newOrdinancesObj[selectedOrdinance] = res.ordinances[selectedOrdinance]
    })
    return {
      person: res.person,
      ordinances: newOrdinancesObj,
    }
  })
}

/**
 * Takes a reservation and returns objects where the reserver details can be retrieved.
 * @param {object} reservation - The reservation row object.
 * @returns {object} - Returns an ordinance object as primary reserver and alternateReservationDetails as secondary reserver.
 */
export const getReservers = (reservation) => {
  const ordinances = Object.values(reservation?.displayedOrdinances || reservation?.ordinances || {})
  let secondaryReserver = {}
  let primaryReserver = {}
  const secondaryOrdinances = Object.values(ordinances).filter((ord) => ord.alternateReservationDetails)

  secondaryReserver = secondaryOrdinances?.[0]?.alternateReservationDetails || ''
  primaryReserver = ordinances[0] || ''
  return { secondaryReserver, primaryReserver }
}

/**
 * Returns a string of all the ordinance keys.
 * @param {object} reservation - The reservation row object.
 * @returns {string} - only the displayed ordinances as a string
 */
export const getOrdinancesString = (reservation) => {
  // cannot use displayed ordinances here since filters changes those and we dont want to change the rowName
  return Object.keys(reservation.ordinances || {})
    .filter((key) => {
      return reservation.ordinances[key]?.reserve
    })
    .toString()
}

/**
 * Takes a reservation object and returns a unique id.
 * @param {object} reservation - The reservation row object.
 * @param {bool} inWaitingState - If the reservation is in a family group and in a waiting state.
 * @returns {string} - Unique reservation id
 */
export const getUniqueReservationId = (reservation, inWaitingState) => {
  const relationshipId = reservation.relationships?.[0]?.id || ''
  // must maintain unique id for split rows (waiting state in family groups) to avoid duplicate rows from displaying
  const waitingState = inWaitingState ? 'inWaitingState' : ''

  return `${reservation.person?.id}${reservation.type}${relationshipId}${waitingState}${getOrdinancesString(
    reservation
  )}`
}

/**
 * Determines whether any of the ordinances holds a truthy value for a given key.
 * @param {object} ordinances - The ordinances object (holds baptism, confirmation, etc.).
 * @param {string} permission - The key to search for in all the ordianances.
 * @returns {boolean} - Whether or not any of the ordinances are truthy for the given key.
 */
export const filterOrdinances = (ordinances, permission) => {
  if (!ordinances) return null
  return Object.values(ordinances).reduce((acc, ordinance) => {
    if (acc) return acc
    return ordinance[permission]
  }, false)
}
/**
 * Determines whether any of the owned ordinances holds a truthy value for a given key.
 * @param {object} ordinances - The object that holds the details.
 * @param {string} whichDetailsObject - The key to search for in all the ordinances.
 * @param {string} ownerId - The id of the owner to match in the object
 * @returns {boolean} - Whether or not any of the ordinances are truthy for the given key.
 */
// Note: Should we refactor this? It's very similar to the one above...
export const filterOrdinancesByDetailsOwner = (ordinances, whichDetailsObject, ownerId) =>
  Object.values(ordinances).reduce((acc, ordinance) => {
    if (acc) return acc
    return ordinance[whichDetailsObject] && ordinance[whichDetailsObject].ownerId === ownerId
  }, false)

/**
 * Determine the correct Reserved and Expires dates to show depending on "secondary" and "caller" reservations.
 * On My Reservations we show both dates. On Shared we show only the Reserved dates.
 * @param {object} reservation - The reservation row object.
 * @param {string} whichDate - The name of key for the date we're looking for.
 * @param {string} ownerId - The current signed-in user id.
 * @returns {string} ownerDate - The date of the current owner (signed-in user).
 */
export const getOwnerDate = (reservation, whichDate, ownerId) => {
  if (!reservation || !whichDate || !ownerId) {
    return ''
  }

  let ownerDate = (reservation && reservation[whichDate]) || ''

  // Originally the "alternateReservationDetails" object came from the Reservations service as "secondaryReservationDetails".
  // When the service changed it, we decided to have tree-data give us the new object name ("alternateReservationDetails") to avoid any confusion later on.
  // However, we decided to leave HTML, CSS, and JavaScript references with the term "secondary".
  if (reservation?.alternateReservationDetails?.ownerId === ownerId) {
    ownerDate = reservation.alternateReservationDetails[whichDate]
  } else if (reservation?.callerReservationDetails?.ownerId === ownerId) {
    ownerDate = reservation.callerReservationDetails[whichDate]
  }

  return ownerDate
}

export const canActOnReservations = (reservations, permission) => {
  const flattened = reservations.map(({ ordinances }) => Object.values(ordinances)).reduce((a, b) => a.concat(b), [])
  return filterOrdinances(flattened, permission)
}

export const getOrdinanceCheckboxValues = (
  ordinances,
  filteredOrdinances = ['baptism and confirmation', 'initiatory', 'endowment', 'sealingToParents', 'sealingToSpouse'],
  forPrinting = false
) => {
  filteredOrdinances = filteredOrdinances
    .map((ordinance) => (ordinance === 'baptism and confirmation' ? ['baptism', 'confirmation'] : ordinance))
    .flat()
  const checkboxValues = ordinances.reduce((ordinanceCheckboxValues, { ordinance }) => {
    if (filteredOrdinances.includes(ordinance)) ordinanceCheckboxValues[ordinance] = true
    // We still want to have the checkbox present and uncheck if it got filtered out.
    else ordinanceCheckboxValues[ordinance] = false
    return ordinanceCheckboxValues
  }, {})

  function checkAllBeforeEndowment() {
    if (ordinances.some(({ ordinance }) => ordinance === 'initiatory')) checkboxValues.initiatory = true
    checkAllBeforeInitiatory()
  }

  function checkAllBeforeInitiatory() {
    if (ordinances.some(({ ordinance }) => ordinance === 'confirmation')) checkboxValues.confirmation = true
    checkBaptism()
  }

  function checkBaptism() {
    if (ordinances.some(({ ordinance }) => ordinance === 'baptism')) checkboxValues.baptism = true
  }

  if (forPrinting) {
    if (checkboxValues.sealingToParents === true) {
      if (ordinances.some(({ ordinance }) => ordinance === 'endowment')) checkboxValues.endowment = true
      checkAllBeforeEndowment()
    } else if (checkboxValues.endowment === true) {
      checkAllBeforeEndowment()
    } else if (checkboxValues.initiatory === true) {
      checkAllBeforeInitiatory()
    } else if (checkboxValues.confirmation === true) {
      checkBaptism()
    } else if (checkboxValues.baptism === true && ordinances.some(({ ordinance }) => ordinance === 'confirmation')) {
      checkboxValues.confirmation = true
    }
  }
  return checkboxValues
}

/**
 * Simple helper to get tab for fetching correct reservations.
 * @returns {string} react tab that user is on
 */
export function getTab() {
  const pathname = window.location.pathname
  // The pathnames for Ordinances Ready and Receive A Name can have stuff beyond their base path, so handle that.
  if (pathname.includes('/ordinances-ready') || pathname.includes('/ordinancesready')) {
    return 'ordinances-ready'
  }
  // The pathname provided by reservation team for Receive a Name is "/receiveaname/" NOT "/receive-a-name/" - handle exception
  if (pathname.includes('/receive-a-name') || pathname.includes('/receiveaname')) {
    return 'receive-a-name'
  }
  const tabName = pathname.split('/').reverse()[0].toLowerCase()
  switch (tabName) {
    case 'shared':
    case 'reservations':
    case 'completed':
    case 'groups':
      return tabName
    default:
      return 'reservations'
  }
}

export const keepRowsWithPermission = (reservations, permission) =>
  reservations
    .map(({ ordinances, originalRow }) =>
      Object.keys(ordinances).some((key) => ordinances[key][permission]) ? { ...originalRow } : false
    )
    .filter(Boolean)

export const checkCanUnreserve = (reservations) =>
  reservations
    .filter((reservation) => reservation?.ordinances)
    .map(({ ordinances, originalRow }) =>
      Object.keys(ordinances).some((key) => ordinances[key].status === 'NotApplicable' || ordinances[key].canUnreserve)
        ? { ...originalRow }
        : false
    )
    .filter(Boolean)
/**
 * Returns a function with a wait time before it is called.
 * @param {Function} func - The function to be returned with the timeout implemented.
 * @param {number} waitTime - The wait time for the returned debounced function.
 * @returns {Function} func - The debounced function.
 */
export const debouncer = (func, waitTime) => {
  let timeout
  return (...args) => {
    timeout && clearTimeout(timeout)
    timeout = setTimeout(() => {
      func(...args)
      timeout = null
    }, waitTime)
  }
}

/**
 * Small function to return the formatted tab name with a capital first letter, or 'Reservations' if tabName nonsensical.
 * @param {string} tabName - The string to be converted to subPage name
 * @returns {string} subPage - The string which represents subPage name, or 'Reservations' if no tabName was supplied
 */
export const getSubPageFromTab = (tabName) => {
  switch (tabName) {
    case 'shared':
      return 'Shared'
    case 'reservations':
      return 'Reservations'
    case 'groups':
      return 'Groups'
    case 'completed':
      return 'Completed'
    case 'ordinances-ready':
      return 'OrdinancesReady'
    case 'receive-a-name':
      return 'ReceiveAName'
    default:
      return 'Reservations'
  }
}

export function countPrintableOrdinances(reservations, permission, groupId) {
  function compareFunction(ordA, ordB) {
    if (ORDINANCE_ORDER[ordA.ordinance] > ORDINANCE_ORDER[ordB.ordinance]) return 1
    if (ORDINANCE_ORDER[ordA.ordinance] < ORDINANCE_ORDER[ordB.ordinance]) return -1
    return 0
  }
  const ordinanceCounts = reservations
    .filter((reservation) => reservation?.ordinances)
    // First, filter the ordinances keys for the permission we're looking for
    .map(({ ordinances }) =>
      Object.entries(ordinances).filter(
        ([key]) =>
          ordinances[key][permission] &&
          (groupId
            ? ordinances[key].groupId === groupId
            : !ordinances[key].groupId || ordinances[key].alternateReservationDetails)
      )
    )
    .reduce((acc, ordinances) => {
      let mustPrintWithArray = []
      let mustPrintWithArrayIndex
      ordinances.forEach((ordArray, index) => {
        if (ordArray[1].mustPrintWith?.length > mustPrintWithArray.length) {
          mustPrintWithArray = ordArray[1].mustPrintWith
          mustPrintWithArrayIndex = index
        }
      })
      if (mustPrintWithArray.length > 0) {
        mustPrintWithArray.forEach((ordinance) => {
          const key = ordinance.toLowerCase()
          acc[key] = acc[key] || 0
          acc[key] += 1
        })
        let key = ordinances[mustPrintWithArrayIndex][0].toLowerCase()
        if (key.includes('sealing')) {
          key = 'sealingToParents'
        }
        acc[key] = acc[key] || 0
        acc[key] += 1
      } else {
        ordinances.forEach((ordinanceArray) => {
          const ordinance = ordinanceArray[0]
          acc[ordinance] = acc[ordinance] || 0
          acc[ordinance] += 1
        })
      }

      return acc
    }, {})
  const countedOrdinanceArray = Object.keys(ordinanceCounts).map((ordinance) => ({
    ordinance,
    count: ordinanceCounts[ordinance],
  }))

  return countedOrdinanceArray.sort(compareFunction)
}

export const getOrdinances = (ordinances, ordinanceCheckboxes) => {
  const selectedOrdinances = {}
  ordinances.forEach((ordinance) => {
    switch (ordinance.ordinance) {
      case 'baptism':
        if (ordinanceCheckboxes.includes('BAPTISM')) {
          selectedOrdinances.baptism = true
        } else {
          selectedOrdinances.baptism = false
        }
        break
      case 'confirmation':
        if (ordinanceCheckboxes.includes('CONFIRMATION')) {
          selectedOrdinances.confirmation = true
        } else {
          selectedOrdinances.confirmation = false
        }
        break
      case 'initiatory':
        if (ordinanceCheckboxes.includes('INITIATORY')) {
          selectedOrdinances.initiatory = true
        } else {
          selectedOrdinances.initiatory = false
        }
        break
      case 'endowment':
        if (ordinanceCheckboxes.includes('ENDOWMENT')) {
          selectedOrdinances.endowment = true
        } else {
          selectedOrdinances.endowment = false
        }
        break
      case 'sealingToSpouse':
        if (ordinanceCheckboxes.includes('SEALING_TO_SPOUSE')) {
          selectedOrdinances.sealingToSpouse = true
        } else {
          selectedOrdinances.sealingToSpouse = false
        }

        break
      case 'sealingToParents':
        if (ordinanceCheckboxes.includes('SEALING_TO_PARENTS')) {
          selectedOrdinances.sealingToParents = true
        } else {
          selectedOrdinances.sealingToParents = false
        }
        break
      default:
        break
    }
  })
  return selectedOrdinances
}

export function printableMustPrintWithOrdinances(selectedReservations) {
  return [...selectedReservations].map((resObj) => {
    const resObjCopy = { ...resObj, ordinances: { ...resObj.ordinances } }
    const ordWithMustPrintWith = Object.values(resObjCopy.ordinances).sort((ordA, ordB) =>
      (ordA?.mustPrintWith?.length ?? 0) > (ordB?.mustPrintWith?.length ?? 0) ? -1 : 1
    )?.[0]

    ordWithMustPrintWith.mustPrintWith?.forEach((ord) => {
      let key = ord.toLowerCase()
      if (key.includes('sealing')) {
        key = 'sealingToParents'
      }

      const ordinanceCopy = { ...resObj.ordinances[key] }

      resObjCopy.ordinances[key] = ordinanceCopy
      resObjCopy.ordinances[key].canPrint = true

      // Visually they will have the correct status. This wont be fixed for screen readers due to the fact the hover text is ordinance specific and supplied from the backend
      resObjCopy.ordinances[key].status = ordWithMustPrintWith.status
    })
    return resObjCopy
  })
}
